// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useMemo } from 'react';

import { buildChecklist, projectRecord } from '../../bygning/checklist';
import { hierarchyRows, nextSteps, panelKpis, sagenRows } from '../../bygning/overblik';
import { roomsOf } from '../../bygning/rooms';
import { roomsOrSpaces } from '../../bygning/spatial';
import { storeyById } from '../../bygning/storeys';
import { buildSubmission } from '../../bygning/submission';
import { Pill } from '../../components/Pill';
import { Panel } from '../../components/bygning/Panel';
import { useSpaces, useStoreys } from '../../components/bygning/settings/SettingsContext';
import { Strip } from '../../components/bygning/Strip';
import { StripCard } from '../../components/bygning/StripCard';
import { HierarchyBars } from '../../components/bygning/overblik/HierarchyBars';
import { KpiPanel } from '../../components/bygning/overblik/KpiPanel';
import { NextSteps } from '../../components/bygning/overblik/NextSteps';
import { SagenPanel } from '../../components/bygning/overblik/SagenPanel';
import { SagsdataForm } from '../../components/bygning/sagsdata/SagsdataForm';
import { useSagsdataExtras } from '../../components/bygning/sagsdata/useSagsdataExtras';
import { TextLink } from '../../components/TextLink';
import type { ModeComponent } from './modeTypes';
import styles from './OverblikMode.module.css';

/**
 * Overblik (PLAN.md layout contract): the case's record ("Sagen") on the left;
 * the case's figures (Nøgletal), Næste skridt and the Affaldshierarki (the
 * mass per treatment step) on the right, each its own panel; the rooms in the strip — a card focuses the room (`?room=`; its
 * types are listed in Ressourcer's left tile, not here), the breadcrumb's ×
 * (or Esc) clears it. Under a storey focus (`?storey=`, the
 * breadcrumb's middle level) the strip shows only that storey's rooms and
 * the left column says which storey it is. Replaces OverblikPage: its KPI row,
 * circularity bar and quick links live here. Sagsdata (the retired mode) is a
 * dialog over it, open while `?edit=sag`: from Sagen's Rediger, Næste skridt
 * and ⌘K; Luk, Esc or Back closes it. The left tile is hidden on a phone, so
 * there it is reached through Næste skridt and ⌘K.
 */
export const OverblikMode: ModeComponent = ({ data, sel, setSel, actions }) => {
  // One copy of the Sagsdata extras for the panel, the steps and the dialog.
  const sagsExtras = useSagsdataExtras(data.project);
  const { extras, held } = sagsExtras;
  const allRooms = useMemo(() => roomsOf(data.types, data.summary), [data.types, data.summary]);
  const storeys = useStoreys();
  const storey = storeyById(storeys, sel.storey);
  const rooms = useMemo(
    () => (storey ? allRooms.filter((r) => r.id !== null && storey.roomIds.includes(r.id)) : allRooms),
    [allRooms, storey],
  );
  // The building model's spaces (`GET /spaces`) win over the scan's rooms
  // cloud for the count/list when the server has any in scope — an imported
  // Revit/IFC case has no rooms cloud, so `rooms` would always read "0 rum".
  const spaces = useSpaces();
  const roomsOrSpacesInfo = useMemo(
    () => roomsOrSpaces(spaces, storey?.id ?? null, { count: rooms.length, names: rooms.map((r) => r.name) }),
    [spaces, storey, rooms],
  );
  // The next submission to bygningsaffald.dk, as Rapport words it (its client
  // checklist: the server's section verdicts are Rapport's own preview).
  const submission = useMemo(() => {
    if (!data.loaded) return null;
    const project = projectRecord(data.project, extras);
    const input = {
      types: data.types,
      samples: data.samples,
      fractions: data.fractions,
      project,
      resourceKeys: data.resourceKeys,
      resources: data.resources,
    };
    return buildSubmission(buildChecklist(input), {
      ...input,
      photos: data.photos,
      photosFailed: data.photos === null && !data.loading,
      versions: data.reportVersions,
    });
  }, [
    data.loaded,
    data.loading,
    data.project,
    extras,
    data.types,
    data.samples,
    data.fractions,
    data.resourceKeys,
    data.resources,
    data.photos,
    data.reportVersions,
  ]);
  const steps = useMemo(
    () =>
      nextSteps(data.summary, data.loaded ? data.samples : null, data.reportVersions, data.project, {
        queue: data.survey?.counts.queue ?? null,
        fractions: data.fractions,
        extras,
        submission: submission?.next ?? null,
      }),
    [
      data.summary,
      data.loaded,
      data.samples,
      data.reportVersions,
      data.project,
      data.survey,
      data.fractions,
      extras,
      submission,
    ],
  );
  const sagen = useMemo(() => sagenRows(data.project, extras), [data.project, extras]);

  const left = (
    <div className={styles.left}>
      {storey && (
        <Panel>
          <p className={styles.storeyLine}>
            <span>
              Etage: <b className={styles.storeyName}>{storey.name}</b> · {roomsOrSpacesInfo.count} rum
            </span>
            <TextLink
              size="sm"
              nowrap
              onClick={() => setSel({ storey: null })}
              aria-label={`Ryd etagefokus (${storey.name}) — vis hele bygningen`}
            >
              Hele bygningen
            </TextLink>
          </p>
        </Panel>
      )}
      <SagenPanel
        name={data.project?.name ?? ''}
        rows={sagen}
        loaded={data.loaded}
        held={held.length > 0}
        onEdit={() => setSel({ edit: 'sag' })}
      />
    </div>
  );

  const right = (
    <div className={styles.right}>
      <KpiPanel kpis={data.summary ? panelKpis(data.summary) : null} />
      <NextSteps steps={steps} onGo={(target) => setSel({ mode: target.mode, ...target.patch })} />
      {data.summary && <HierarchyBars rows={hierarchyRows(data.summary.circularity)} />}
      {/* Portalled over the whole shell; it sits here because the right tile is always drawn. */}
      {sel.edit === 'sag' && (
        <SagsdataForm
          project={data.project}
          loaded={data.loaded}
          extras={sagsExtras}
          onSave={actions.patchProject}
          onClose={() => setSel({ edit: null })}
        />
      )}
    </div>
  );

  const bottom = (
    <Strip label="Rum" activeKey={sel.room}>
      {roomsOrSpacesInfo.count === 0 && data.loaded ? (
        <p className={styles.stripEmpty}>
          {storey
            ? `Ingen rum med registrerede dele på ${storey.name}.`
            : 'Ingen rum endnu — rummene kommer fra scanningens rumopdeling.'}
        </p>
      ) : roomsOrSpacesInfo.source === 'spaces' ? (
        // The building model's spaces have no link to the survey's parts/types
        // (and no `?room=` id of their own), so the card is name-only and
        // unfocusable — unlike a scan room's.
        roomsOrSpacesInfo.names.map((name, i) => (
          <StripCard key={`space-${i}-${name}`} variant="room" title={name} disabled onClick={() => {}} />
        ))
      ) : (
        rooms.map((r) => (
          <StripCard
            key={r.key}
            variant="room"
            title={r.name}
            figure={r.parts === 1 ? '1 del' : `${r.parts} dele`}
            figureMuted={r.empty}
            meta={`${r.types} ${r.types === 1 ? 'type' : 'typer'}`}
            status={r.empty ? <Pill tone="warn">Ingen dele</Pill> : undefined}
            active={r.id !== null && sel.room === r.id}
            disabled={r.id === null}
            onClick={() => r.id !== null && setSel({ room: sel.room === r.id ? null : r.id })}
          />
        ))
      )}
    </Strip>
  );

  return { leftWide: true, stacked: true, left, right, bottom };
};
